import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocalShipping as TruckIcon,
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
  Upload as UploadIcon,
  Help as HelpIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Description as DocumentIcon,
  LocalPolice as PoliceIcon,
  DirectionsCar as LicenseIcon,
  HealthAndSafety as InsuranceIcon,
  Warning as WarningIcon,
  Gavel as LegalIcon,
  AccountBalance as BankIcon,
  Verified as VerifiedIcon,
  CloudUpload as CloudUploadIcon,
  Map as MapIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  Update as UpdateIcon,
  NoteAlt as NoteAltIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

/**
 * GovernmentComplianceVerification component for verifying logistics companies' compliance with government regulations
 * Provides a comprehensive verification process for government requirements, licenses, and certifications
 * @returns React component for government compliance verification
 */
const GovernmentComplianceVerification: React.FC = () => {
  // State for tracking the current step in the verification process
  const [activeStep, setActiveStep] = useState(0);
  
  // State for loading status
  const [loading, setLoading] = useState(false);
  
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  
  // State for success messages
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for document preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<{name: string, file: File | null}>({name: '', file: null});
  
  // State for verification status dialog
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  
  // State for storing compliance verification data
  const [complianceData, setComplianceData] = useState({
    // KRA Compliance
    hasKRA: false,
    kraPinNumber: '',
    kraPinVerified: false,
    kraTaxComplianceCertificate: '',
    kraTaxComplianceExpiryDate: '',
    
    // NTSA Compliance
    hasNTSA: false,
    ntsaLicenseNumber: '',
    ntsaLicenseVerified: false,
    ntsaLicenseExpiryDate: '',
    ntsaInspectionCertificate: '',
    ntsaInspectionExpiryDate: '',
    
    // Business Permits
    hasBusinessPermit: false,
    businessPermitNumber: '',
    businessPermitVerified: false,
    businessPermitExpiryDate: '',
    businessPermitCounty: '',
    
    // Transport Operator License
    hasTransportOperatorLicense: false,
    transportOperatorLicenseNumber: '',
    transportOperatorLicenseVerified: false,
    transportOperatorLicenseExpiryDate: '',
    transportOperatorCategory: '',
    
    // Environmental Compliance
    hasEnvironmentalCompliance: false,
    environmentalComplianceCertNumber: '',
    environmentalComplianceVerified: false,
    environmentalComplianceExpiryDate: '',
    environmentalComplianceAuthority: '',
    
    // Insurance Compliance
    hasInsurance: false,
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceVerified: false,
    insuranceExpiryDate: '',
    
    // Goods in Transit Insurance
    hasGoodsInTransitInsurance: false,
    goodsInTransitInsuranceProvider: '',
    goodsInTransitPolicyNumber: '',
    goodsInTransitInsuranceVerified: false,
    goodsInTransitExpiryDate: '',
    
    // Public Liability Insurance
    hasLiabilityInsurance: false,
    liabilityInsuranceProvider: '',
    liabilityInsurancePolicyNumber: '',
    liabilityInsuranceVerified: false,
    liabilityInsuranceExpiryDate: '',
    
    // Vehicle Tracking Compliance
    hasVehicleTracking: false,
    trackingSystemProvider: '',
    trackingSystemVerified: false,
    trackingSystemCertificate: '',
    
    // Driver Verification
    hasDriverVerification: false,
    driverVerificationProcess: '',
    driverVerificationCompliant: false,
    backgroundCheckProcess: '',
    
    // Verification Status
    overallComplianceStatus: 'pending', // 'pending', 'compliant', 'non-compliant', 'conditionally-compliant'
    complianceNotes: '',
    verificationDate: '',
    verifiedBy: '',
    nextReviewDate: '',
  });
  
  // State for file uploads
  const [documents, setDocuments] = useState({
    kraPinCertificate: null as File | null,
    kraTaxComplianceCertificate: null as File | null,
    ntsaLicense: null as File | null,
    ntsaInspectionCertificate: null as File | null,
    businessPermit: null as File | null,
    transportOperatorLicense: null as File | null,
    environmentalComplianceCert: null as File | null,
    insuranceCertificate: null as File | null,
    goodsInTransitInsurance: null as File | null,
    liabilityInsurance: null as File | null,
    vehicleTrackingCertificate: null as File | null,
    driverVerificationDocuments: null as File | null,
  });
  
  // Steps in the verification process
  const steps = [
    'KRA & Tax Compliance',
    'NTSA Compliance',
    'Business Permits',
    'Transport Licenses',
    'Environmental Compliance',
    'Insurance Verification',
    'Vehicle Tracking',
    'Driver Verification',
    'Compliance Summary'
  ];
  
  // Transport operator categories
  const transportOperatorCategories = [
    'General Cargo',
    'Hazardous Materials',
    'Passenger Transport',
    'Agricultural Produce',
    'Construction Materials',
    'Refrigerated Goods',
    'Cross-Border Transport',
    'Express Delivery'
  ];
  
  // Environmental compliance authorities
  const environmentalAuthorities = [
    'National Environment Management Authority (NEMA)',
    'County Environmental Department',
    'Ministry of Environment and Forestry',
    'Other'
  ];
  
  // Insurance providers
  const insuranceProviders = [
    'Jubilee Insurance',
    'APA Insurance',
    'Britam',
    'CIC Insurance',
    'Heritage Insurance',
    'Madison Insurance',
    'UAP Insurance',
    'Other'
  ];
  
  // Vehicle tracking providers
  const trackingProviders = [
    'Safaricom',
    'Car Track',
    'Digital Tracking Technologies',
    'Arvento',
    'Tramigo',
    'Other'
  ];
  
  /**
   * Handles changes to form fields
   * @param field - The field name to update
   * @param value - The new value for the field
   */
  const handleChange = (field: string, value: any) => {
    setComplianceData({
      ...complianceData,
      [field]: value
    });
  };
  
  /**
   * Handles changes to checkbox arrays (multiple selection)
   * @param field - The field name to update
   * @param value - The value to toggle in the array
   */
  const handleArrayChange = (field: string, value: string) => {
    const currentArray = complianceData[field as keyof typeof complianceData] as unknown as string[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setComplianceData({
      ...complianceData,
      [field]: newArray
    });
  };
  
  /**
   * Handles file uploads for document verification
   * @param documentType - The type of document being uploaded
   * @param file - The file object from the input
   */
  const handleFileUpload = (documentType: string, file: File | null) => {
    setDocuments({
      ...documents,
      [documentType]: file
    });
  };
  
  /**
   * Opens the document preview dialog
   * @param documentName - The name of the document to preview
   * @param file - The file to preview
   */
  const handlePreviewDocument = (documentName: string, file: File | null) => {
    setPreviewDocument({ name: documentName, file });
    setPreviewOpen(true);
  };
  
  /**
   * Closes the document preview dialog
   */
  const handleClosePreview = () => {
    setPreviewOpen(false);
  };
  
  /**
   * Opens the verification status dialog
   */
  const handleOpenVerificationDialog = () => {
    setVerificationDialogOpen(true);
  };
  
  /**
   * Closes the verification status dialog
   */
  const handleCloseVerificationDialog = () => {
    setVerificationDialogOpen(false);
  };
  
  /**
   * Moves to the next step in the verification process
   */
  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };
  
  /**
   * Moves to the previous step in the verification process
   */
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  /**
   * Submits the verification form
   */
  const handleSubmit = () => {
    setLoading(true);
    setError(null);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSuccess('Verification completed successfully! The compliance status has been updated.');
      
      // Calculate overall compliance status based on verification results
      const mandatoryCompliance = [
        complianceData.kraPinVerified,
        complianceData.ntsaLicenseVerified,
        complianceData.businessPermitVerified,
        complianceData.insuranceVerified
      ];
      
      const additionalCompliance = [
        complianceData.transportOperatorLicenseVerified,
        complianceData.environmentalComplianceVerified,
        complianceData.goodsInTransitInsuranceVerified,
        complianceData.liabilityInsuranceVerified,
        complianceData.trackingSystemVerified,
        complianceData.driverVerificationCompliant
      ];
      
      const mandatoryCompliant = mandatoryCompliance.every(item => item === true);
      const additionalCompliantCount = additionalCompliance.filter(item => item === true).length;
      
      let overallStatus = 'pending';
      
      if (mandatoryCompliant) {
        if (additionalCompliantCount >= 4) {
          overallStatus = 'compliant';
        } else if (additionalCompliantCount >= 2) {
          overallStatus = 'conditionally-compliant';
        } else {
          overallStatus = 'non-compliant';
        }
      } else {
        overallStatus = 'non-compliant';
      }
      
      // Update compliance data with verification results
      setComplianceData({
        ...complianceData,
        overallComplianceStatus: overallStatus,
        verificationDate: new Date().toISOString().split('T')[0],
        verifiedBy: 'System Verification',
        nextReviewDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 6 months later
      });
      
      // In a real app, this would send the data to the server
      console.log('Compliance Data:', complianceData);
      console.log('Documents:', documents);
    }, 2000);
  };
  
  /**
   * Renders the KRA and tax compliance step
   * @returns JSX for the KRA and tax compliance step
   */
  const renderKRATaxCompliance = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e3a8a', display: 'flex', alignItems: 'center' }}>
        <BankIcon sx={{ mr: 1 }} />
        KRA & Tax Compliance Verification
      </Typography>
      <Alert 
        severity="info" 
        icon={<InfoIcon />}
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          '& .MuiAlert-icon': {
            color: '#3b82f6'
          }
        }}
      >
        Verify the company's KRA PIN and tax compliance status. All logistics companies must be registered with KRA and have a valid tax compliance certificate.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={complianceData.hasKRA}
                  onChange={(e) => handleChange('hasKRA', e.target.checked)}
                  sx={{
                    color: '#64748b',
                    '&.Mui-checked': {
                      color: '#1e40af',
                    },
                  }}
                />
              }
              label={<Typography sx={{ fontWeight: 500 }}>Company has KRA PIN</Typography>}
            />
          </Paper>
        </Grid>
        
        {complianceData.hasKRA && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="KRA PIN Number"
                value={complianceData.kraPinNumber}
                onChange={(e) => handleChange('kraPinNumber', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VerifiedUserIcon sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: complianceData.kraPinVerified ? '#ecfdf5' : '#f8fafc', borderRadius: 2, border: `1px solid ${complianceData.kraPinVerified ? '#d1fae5' : '#e2e8f0'}`, height: '100%', display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={complianceData.kraPinVerified}
                      onChange={(e) => handleChange('kraPinVerified', e.target.checked)}
                      sx={{
                        color: '#64748b',
                        '&.Mui-checked': {
                          color: '#10b981',
                        },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 500 }}>KRA PIN Verified</Typography>}
                />
                {complianceData.kraPinVerified && <CheckIcon sx={{ ml: 1, color: '#10b981' }} />}
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center' }}>
                  <CloudUploadIcon sx={{ mr: 1, color: '#3b82f6' }} />
                  KRA PIN Certificate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    sx={{
                      borderRadius: 28,
                      px: 3,
                      py: 1,
                      borderColor: '#3b82f6',
                      color: '#3b82f6',
                      '&:hover': {
                        borderColor: '#1e40af',
                        bgcolor: 'rgba(59, 130, 246, 0.04)'
                      }
                    }}
                  >
                    Upload Certificate
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files ? e.target.files[0] : null;
                        handleFileUpload('kraPinCertificate', file);
                      }}
                    />
                  </Button>
                  {documents.kraPinCertificate ? (
                    <Paper elevation={0} sx={{ 
                      p: 1, 
                      pl: 2,
                      bgcolor: '#e0f2fe', 
                      borderRadius: 28, 
                      display: 'flex', 
                      alignItems: 'center',
                      border: '1px solid #bae6fd',
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}>
                      <DocumentIcon sx={{ color: '#0284c7', mr: 1 }} />
                      <Typography variant="body2" sx={{ color: '#0369a1', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {documents.kraPinCertificate.name}
                      </Typography>
                      <Tooltip title="Preview Document">
                        <IconButton
                          size="small"
                          onClick={() => handlePreviewDocument('KRA PIN Certificate', documents.kraPinCertificate)}
                          sx={{ color: '#0284c7' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                      No document uploaded
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 3, borderColor: '#e2e8f0' }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center' }}>
                <VerifiedIcon sx={{ mr: 1, color: '#3b82f6' }} />
                Tax Compliance Certificate
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Compliance Certificate Number"
                value={complianceData.kraTaxComplianceCertificate}
                onChange={(e) => handleChange('kraTaxComplianceCertificate', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LegalIcon sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Compliance Expiry Date"
                type="date"
                value={complianceData.kraTaxComplianceExpiryDate}
                onChange={(e) => handleChange('kraTaxComplianceExpiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center' }}>
                  <CloudUploadIcon sx={{ mr: 1, color: '#3b82f6' }} />
                  Tax Compliance Certificate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    sx={{
                      borderRadius: 28,
                      px: 3,
                      py: 1,
                      borderColor: '#3b82f6',
                      color: '#3b82f6',
                      '&:hover': {
                        borderColor: '#1e40af',
                        bgcolor: 'rgba(59, 130, 246, 0.04)'
                      }
                    }}
                  >
                    Upload Certificate
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files ? e.target.files[0] : null;
                        handleFileUpload('kraTaxComplianceCertificate', file);
                      }}
                    />
                  </Button>
                  {documents.kraTaxComplianceCertificate ? (
                    <Paper elevation={0} sx={{ 
                      p: 1, 
                      pl: 2,
                      bgcolor: '#e0f2fe', 
                      borderRadius: 28, 
                      display: 'flex', 
                      alignItems: 'center',
                      border: '1px solid #bae6fd',
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}>
                      <DocumentIcon sx={{ color: '#0284c7', mr: 1 }} />
                      <Typography variant="body2" sx={{ color: '#0369a1', flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {documents.kraTaxComplianceCertificate.name}
                      </Typography>
                      <Tooltip title="Preview Document">
                        <IconButton
                          size="small"
                          onClick={() => handlePreviewDocument('Tax Compliance Certificate', documents.kraTaxComplianceCertificate)}
                          sx={{ color: '#0284c7' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                      No document uploaded
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the NTSA compliance step
   * @returns JSX for the NTSA compliance step
   */
  const renderNTSACompliance = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        NTSA Compliance Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify the company's compliance with National Transport and Safety Authority (NTSA) regulations. All logistics companies must have valid NTSA licenses and vehicle inspection certificates.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={complianceData.hasNTSA}
                onChange={(e) => handleChange('hasNTSA', e.target.checked)}
              />
            }
            label="Company has NTSA License"
          />
        </Grid>
        
        {complianceData.hasNTSA && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="NTSA License Number"
                value={complianceData.ntsaLicenseNumber}
                onChange={(e) => handleChange('ntsaLicenseNumber', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="NTSA License Expiry Date"
                type="date"
                value={complianceData.ntsaLicenseExpiryDate}
                onChange={(e) => handleChange('ntsaLicenseExpiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={complianceData.ntsaLicenseVerified}
                    onChange={(e) => handleChange('ntsaLicenseVerified', e.target.checked)}
                  />
                }
                label="NTSA License Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload NTSA License
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('ntsaLicense', file);
                    }}
                  />
                </Button>
                {documents.ntsaLicense && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.ntsaLicense.name}
                    </Typography>
                    <IconButton
                      onClick={() => handlePreviewDocument('NTSA License', documents.ntsaLicense)}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Vehicle Inspection Certificates
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="NTSA Inspection Certificate Number"
                value={complianceData.ntsaInspectionCertificate}
                onChange={(e) => handleChange('ntsaInspectionCertificate', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Inspection Certificate Expiry Date"
                type="date"
                value={complianceData.ntsaInspectionExpiryDate}
                onChange={(e) => handleChange('ntsaInspectionExpiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Inspection Certificate
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('ntsaInspectionCertificate', file);
                    }}
                  />
                </Button>
                {documents.ntsaInspectionCertificate && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.ntsaInspectionCertificate.name}
                    </Typography>
                    <IconButton
                      onClick={() => handlePreviewDocument('NTSA Inspection Certificate', documents.ntsaInspectionCertificate)}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the business permits step
   * @returns JSX for the business permits step
   */
  const renderBusinessPermits = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Business Permits Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify the company's business permits issued by county governments. All logistics companies must have valid business permits for their operations.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={complianceData.hasBusinessPermit}
                onChange={(e) => handleChange('hasBusinessPermit', e.target.checked)}
              />
            }
            label="Company has Business Permit"
          />
        </Grid>
        
        {complianceData.hasBusinessPermit && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Business Permit Number"
                value={complianceData.businessPermitNumber}
                onChange={(e) => handleChange('businessPermitNumber', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Business Permit Expiry Date"
                type="date"
                value={complianceData.businessPermitExpiryDate}
                onChange={(e) => handleChange('businessPermitExpiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="County of Issue"
                value={complianceData.businessPermitCounty}
                onChange={(e) => handleChange('businessPermitCounty', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={complianceData.businessPermitVerified}
                    onChange={(e) => handleChange('businessPermitVerified', e.target.checked)}
                  />
                }
                label="Business Permit Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Business Permit
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('businessPermit', file);
                    }}
                  />
                </Button>
                {documents.businessPermit && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.businessPermit.name}
                    </Typography>
                    <IconButton
                      onClick={() => handlePreviewDocument('Business Permit', documents.businessPermit)}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the transport licenses step
   * @returns JSX for the transport licenses step
   */
  const renderTransportLicenses = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Transport Operator License Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify the company's transport operator licenses. Specialized transport services require specific operator licenses.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={complianceData.hasTransportOperatorLicense}
                onChange={(e) => handleChange('hasTransportOperatorLicense', e.target.checked)}
              />
            }
            label="Company has Transport Operator License"
          />
        </Grid>
        
        {complianceData.hasTransportOperatorLicense && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Transport Operator License Number"
                value={complianceData.transportOperatorLicenseNumber}
                onChange={(e) => handleChange('transportOperatorLicenseNumber', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="License Expiry Date"
                type="date"
                value={complianceData.transportOperatorLicenseExpiryDate}
                onChange={(e) => handleChange('transportOperatorLicenseExpiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Transport Operator Category</InputLabel>
                <Select
                  value={complianceData.transportOperatorCategory}
                  onChange={(e) => handleChange('transportOperatorCategory', e.target.value)}
                  label="Transport Operator Category"
                >
                  {transportOperatorCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the environmental compliance step
   * @returns JSX for the environmental compliance step
   */
  const renderEnvironmentalCompliance = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Environmental Compliance Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify the company's compliance with environmental regulations. All logistics companies must have valid environmental compliance certificates.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={complianceData.hasEnvironmentalCompliance}
                onChange={(e) => handleChange('hasEnvironmentalCompliance', e.target.checked)}
              />
            }
            label="Company has Environmental Compliance Certificate"
          />
        </Grid>
        
        {complianceData.hasEnvironmentalCompliance && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Environmental Compliance Certificate Number"
                value={complianceData.environmentalComplianceCertNumber}
                onChange={(e) => handleChange('environmentalComplianceCertNumber', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={complianceData.environmentalComplianceVerified}
                    onChange={(e) => handleChange('environmentalComplianceVerified', e.target.checked)}
                  />
                }
                label="Environmental Compliance Certificate Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Environmental Compliance Certificate
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('environmentalComplianceCert', file);
                    }}
                  />
                </Button>
                {documents.environmentalComplianceCert && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.environmentalComplianceCert.name}
                    </Typography>
                    <IconButton
                      onClick={() => handlePreviewDocument('Environmental Compliance Certificate', documents.environmentalComplianceCert)}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the insurance verification step
   * @returns JSX for the insurance verification step
   */
  const renderInsuranceVerification = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Insurance Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify the company's insurance compliance. All logistics companies must have valid insurance policies.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={complianceData.hasInsurance}
                onChange={(e) => handleChange('hasInsurance', e.target.checked)}
              />
            }
            label="Company has Insurance"
          />
        </Grid>
        
        {complianceData.hasInsurance && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Provider"
                value={complianceData.insuranceProvider}
                onChange={(e) => handleChange('insuranceProvider', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Policy Number"
                value={complianceData.insurancePolicyNumber}
                onChange={(e) => handleChange('insurancePolicyNumber', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={complianceData.insuranceVerified}
                    onChange={(e) => handleChange('insuranceVerified', e.target.checked)}
                  />
                }
                label="Insurance Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Insurance Certificate
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('insuranceCertificate', file);
                    }}
                  />
                </Button>
                {documents.insuranceCertificate && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.insuranceCertificate.name}
                    </Typography>
                    <IconButton
                      onClick={() => handlePreviewDocument('Insurance Certificate', documents.insuranceCertificate)}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the vehicle tracking step
   * @returns JSX for the vehicle tracking step
   */
  const renderVehicleTracking = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Vehicle Tracking Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify the company's vehicle tracking compliance. All logistics companies must have a valid vehicle tracking system.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={complianceData.hasVehicleTracking}
                onChange={(e) => handleChange('hasVehicleTracking', e.target.checked)}
              />
            }
            label="Company has Vehicle Tracking System"
          />
        </Grid>
        
        {complianceData.hasVehicleTracking && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tracking System Provider"
                value={complianceData.trackingSystemProvider}
                onChange={(e) => handleChange('trackingSystemProvider', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={complianceData.trackingSystemVerified}
                    onChange={(e) => handleChange('trackingSystemVerified', e.target.checked)}
                  />
                }
                label="Tracking System Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Tracking System Certificate
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('vehicleTrackingCertificate', file);
                    }}
                  />
                </Button>
                {documents.vehicleTrackingCertificate && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.vehicleTrackingCertificate.name}
                    </Typography>
                    <IconButton
                      onClick={() => handlePreviewDocument('Tracking System Certificate', documents.vehicleTrackingCertificate)}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the driver verification step
   * @returns JSX for the driver verification step
   */
  const renderDriverVerification = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Driver Verification
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Verify the company's driver compliance. All logistics companies must have compliant drivers.
      </Alert>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={complianceData.hasDriverVerification}
                onChange={(e) => handleChange('hasDriverVerification', e.target.checked)}
              />
            }
            label="Company has Driver Verification"
          />
        </Grid>
        
        {complianceData.hasDriverVerification && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Driver Verification Process"
                value={complianceData.driverVerificationProcess}
                onChange={(e) => handleChange('driverVerificationProcess', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={complianceData.driverVerificationCompliant}
                    onChange={(e) => handleChange('driverVerificationCompliant', e.target.checked)}
                  />
                }
                label="Driver Verified"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Driver Verification Documents
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      handleFileUpload('driverVerificationDocuments', file);
                    }}
                  />
                </Button>
                {documents.driverVerificationDocuments && (
                  <>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {documents.driverVerificationDocuments.name}
                    </Typography>
                    <IconButton
                      onClick={() => handlePreviewDocument('Driver Verification Documents', documents.driverVerificationDocuments)}
                    >
                      <DocumentIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
  
  /**
   * Renders the compliance summary step
   * @returns JSX for the compliance summary step
   */
  const renderComplianceSummary = () => {
    // Function to determine status color and icon
    const getStatusInfo = (status: string) => {
      switch(status) {
        case 'compliant':
          return { color: '#10b981', icon: <CheckIcon />, label: 'Compliant' };
        case 'conditionally-compliant':
          return { color: '#f59e0b', icon: <WarningIcon />, label: 'Conditionally Compliant' };
        case 'non-compliant':
          return { color: '#ef4444', icon: <CancelIcon />, label: 'Non-Compliant' };
        default:
          return { color: '#6b7280', icon: <HelpIcon />, label: 'Pending' };
      }
    };
    
    const statusInfo = getStatusInfo(complianceData.overallComplianceStatus);
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e3a8a', mb: 3 }}>
          <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Compliance Summary
        </Typography>
        <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
          Review the company's compliance status and notes.
        </Alert>
        
        <Card sx={{ mb: 4, borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
          <Box sx={{ p: 2, bgcolor: statusInfo.color, color: 'white', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {statusInfo.icon}
            </Box>
            <Typography variant="h6">
              Overall Compliance Status: {statusInfo.label}
            </Typography>
          </Box>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#475569' }}>
                    <CalendarTodayIcon sx={{ fontSize: 'small', mr: 1, verticalAlign: 'middle' }} />
                    Verification Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {complianceData.verificationDate || 'Not verified yet'}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#475569' }}>
                    <PersonIcon sx={{ fontSize: 'small', mr: 1, verticalAlign: 'middle' }} />
                    Verified By
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {complianceData.verifiedBy || 'Not verified yet'}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#475569' }}>
                    <UpdateIcon sx={{ fontSize: 'small', mr: 1, verticalAlign: 'middle' }} />
                    Next Review Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {complianceData.nextReviewDate || 'Not scheduled'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        <Card sx={{ mb: 4, borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e3a8a' }}>
              <NoteAltIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Compliance Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={complianceData.complianceNotes}
              onChange={(e) => handleChange('complianceNotes', e.target.value)}
              placeholder="Add detailed notes about compliance status, issues, and recommendations..."
              sx={{ 
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                },
              }}
            />
          </CardContent>
        </Card>
        
        <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e3a8a' }}>
              <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Compliance Breakdown
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: complianceData.kraPinVerified ? '#ecfdf5' : '#fef2f2', 
                  borderRadius: 2,
                  border: `1px solid ${complianceData.kraPinVerified ? '#d1fae5' : '#fee2e2'}`,
                  height: '100%'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: complianceData.kraPinVerified ? '#065f46' : '#b91c1c' }}>
                    KRA Compliance
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {complianceData.kraPinVerified ? 
                      <CheckIcon sx={{ color: '#10b981', mr: 1 }} /> : 
                      <CancelIcon sx={{ color: '#ef4444', mr: 1 }} />}
                    <Typography variant="body2">
                      {complianceData.kraPinVerified ? 'Verified' : 'Not Verified'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: complianceData.ntsaLicenseVerified ? '#ecfdf5' : '#fef2f2', 
                  borderRadius: 2,
                  border: `1px solid ${complianceData.ntsaLicenseVerified ? '#d1fae5' : '#fee2e2'}`,
                  height: '100%'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: complianceData.ntsaLicenseVerified ? '#065f46' : '#b91c1c' }}>
                    NTSA Compliance
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {complianceData.ntsaLicenseVerified ? 
                      <CheckIcon sx={{ color: '#10b981', mr: 1 }} /> : 
                      <CancelIcon sx={{ color: '#ef4444', mr: 1 }} />}
                    <Typography variant="body2">
                      {complianceData.ntsaLicenseVerified ? 'Verified' : 'Not Verified'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: complianceData.businessPermitVerified ? '#ecfdf5' : '#fef2f2', 
                  borderRadius: 2,
                  border: `1px solid ${complianceData.businessPermitVerified ? '#d1fae5' : '#fee2e2'}`,
                  height: '100%'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: complianceData.businessPermitVerified ? '#065f46' : '#b91c1c' }}>
                    Business Permit
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {complianceData.businessPermitVerified ? 
                      <CheckIcon sx={{ color: '#10b981', mr: 1 }} /> : 
                      <CancelIcon sx={{ color: '#ef4444', mr: 1 }} />}
                    <Typography variant="body2">
                      {complianceData.businessPermitVerified ? 'Verified' : 'Not Verified'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: complianceData.insuranceVerified ? '#ecfdf5' : '#fef2f2', 
                  borderRadius: 2,
                  border: `1px solid ${complianceData.insuranceVerified ? '#d1fae5' : '#fee2e2'}`,
                  height: '100%'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: complianceData.insuranceVerified ? '#065f46' : '#b91c1c' }}>
                    Insurance
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {complianceData.insuranceVerified ? 
                      <CheckIcon sx={{ color: '#10b981', mr: 1 }} /> : 
                      <CancelIcon sx={{ color: '#ef4444', mr: 1 }} />}
                    <Typography variant="body2">
                      {complianceData.insuranceVerified ? 'Verified' : 'Not Verified'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };
  
  /**
   * Renders the verification status dialog
   * @returns JSX for the verification status dialog
   */
  const renderVerificationStatusDialog = () => {
    const isSuccess = Boolean(success);
    const statusColor = isSuccess ? '#10b981' : '#ef4444';
    const statusIcon = isSuccess ? <CheckCircleIcon sx={{ fontSize: 40, color: statusColor }} /> : <ErrorIcon sx={{ fontSize: 40, color: statusColor }} />;
    const statusTitle = isSuccess ? 'Verification Successful' : 'Verification Error';
    
    return (
      <Dialog 
        open={verificationDialogOpen} 
        onClose={handleCloseVerificationDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 500
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: isSuccess ? '#ecfdf5' : '#fef2f2', 
          color: isSuccess ? '#065f46' : '#b91c1c',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 600
        }}>
          {statusIcon}
          {statusTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" sx={{ color: '#1f2937' }}>
            {success || error}
          </Typography>
          
          {isSuccess && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #dcfce7' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#166534', mb: 1 }}>
                <InfoIcon sx={{ fontSize: 'small', mr: 1, verticalAlign: 'middle' }} />
                Next Steps
              </Typography>
              <Typography variant="body2" sx={{ color: '#166534' }}>
                Your compliance verification has been processed. You can now proceed with logistics operations based on your compliance status.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseVerificationDialog}
            variant="contained"
            sx={{ 
              borderRadius: 28, 
              px: 3,
              bgcolor: isSuccess ? '#10b981' : '#6b7280',
              '&:hover': {
                bgcolor: isSuccess ? '#059669' : '#4b5563'
              }
            }}
          >
            {isSuccess ? 'Continue' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 4 }, bgcolor: '#f9fafb', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          fontWeight: 600, 
          color: '#1e3a8a', 
          mb: 3, 
          textAlign: 'center',
          borderBottom: '2px solid #e5e7eb',
          pb: 2
        }}
      >
        <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#1e40af' }} />
        Government Compliance Verification
      </Typography>
      
      <Paper elevation={0} sx={{ mb: 4, p: 3, bgcolor: 'white', borderRadius: 2 }}>
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel
          sx={{
            '& .MuiStepLabel-root .Mui-completed': {
              color: '#10b981', // green for completed steps
            },
            '& .MuiStepLabel-root .Mui-active': {
              color: '#3b82f6', // blue for active step
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>
      
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mb: 4, bgcolor: 'white', borderRadius: 2 }}>
        {activeStep === 0 && renderKRATaxCompliance()}
        {activeStep === 1 && renderNTSACompliance()}
        {activeStep === 2 && renderBusinessPermits()}
        {activeStep === 3 && renderTransportLicenses()}
        {activeStep === 4 && renderEnvironmentalCompliance()}
        {activeStep === 5 && renderInsuranceVerification()}
        {activeStep === 6 && renderVehicleTracking()}
        {activeStep === 7 && renderDriverVerification()}
        {activeStep === 8 && renderComplianceSummary()}
      </Paper>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        {activeStep > 0 ? (
          <Button 
            onClick={handleBack} 
            variant="outlined" 
            startIcon={<ExpandMoreIcon sx={{ transform: 'rotate(90deg)' }} />}
            sx={{ borderRadius: 28, px: 3 }}
          >
            Back
          </Button>
        ) : <Box />}
        
        {activeStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext} 
            variant="contained" 
            endIcon={<ExpandMoreIcon sx={{ transform: 'rotate(270deg)' }} />}
            sx={{ 
              borderRadius: 28, 
              px: 3, 
              bgcolor: '#1e40af',
              '&:hover': {
                bgcolor: '#1e3a8a'
              }
            }}
          >
            Next
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            endIcon={<CheckIcon />}
            disabled={loading}
            sx={{ 
              borderRadius: 28, 
              px: 4, 
              bgcolor: '#10b981',
              '&:hover': {
                bgcolor: '#059669'
              }
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ color: 'white', mr: 1 }} />
                Processing
              </>
            ) : 'Submit'}
          </Button>
        )}
      </Box>
      
      {renderVerificationStatusDialog()}
    </Box>
  );
};

export default GovernmentComplianceVerification;